import {
  collection,
  onSnapshot,
  query,
  where,
  getDocs,
  doc,
  setDoc,
  addDoc,
  updateDoc,
  orderBy,
  limit,
  startAfter,
} from "firebase/firestore";
import { db } from "../../@shared/firebase/config"; // adjust import to your project
import {
  get,
  getDatabase,
  ref,
  onDisconnect,
  set,
  query as QUERY,
  orderByChild,
  equalTo,
  onChildAdded,
  onChildChanged,
  onChildRemoved,
  off,
  update,
} from "firebase/database";

// import { v4 as uuidv4 } from "uuid";

export const createRoom = async (userId, settings) => {
  const accessCode = Math.floor(1000000 + Math.random() * 9000000);

  const roomRef = doc(collection(db, "rooms"));
  const roomId = roomRef.id;

  const roomData = {
    accessCode,
    amountOfPlayers: settings.amountOfPlayers || 2,
    botFill: settings.botFill || false,
    playerIds: [userId],
    players: settings.players || [],
    public: settings.public || false,
    roomOwner: userId,
    status: "lobby",
    teams: settings.teams || false,
    timedTurns: settings.timedTurns || false,
    turnTime: settings.turnTime || 30,
    ranked: settings.ranked || false,
    pve: settings.pve || false,
  };

  // 1. Create the room doc
  await setDoc(roomRef, roomData);

  // 2. Add initial message to chatThreadData subcollection
  const initialChatRef = doc(collection(db, "rooms", roomId, "chatThreadData"));
  await setDoc(initialChatRef, {
    sender: "system",
    message: "Welcome to the room!",
    createdAt: new Date(),
  });

  return { id: roomId, ...roomData };
};

export const joinRoom = async (accessCode, userData) => {
  const q = query(
    collection(db, "rooms"),
    where("accessCode", "==", accessCode)
  );
  const querySnapshot = await getDocs(q);

  if (querySnapshot.empty) {
    throw new Error("Room not found");
  }

  const roomDoc = querySnapshot.docs[0];
  const roomData = roomDoc.data();

  if (roomData.playerIds.includes(userData.id)) {
    return { id: roomDoc.id, ...roomData };
  }

  if (roomData.playerIds.length >= roomData.amountOfPlayers) {
    throw new Error("Room is full");
  }

  const updatedPlayerIds = [...roomData.playerIds, userData.id];
  const updatedPlayers = [...roomData.players, userData];

  await updateDoc(doc(db, "rooms", roomDoc.id), {
    playerIds: updatedPlayerIds,
    players: updatedPlayers,
  });

  return {
    id: roomDoc.id,
    ...roomData,
    playerIds: updatedPlayerIds,
    players: updatedPlayers,
  };
};

export const getLobbyRooms = async (startAfterDoc = null, pageSize = 100) => {
  try {
    const baseQuery = query(
      collection(db, "rooms"),
      where("status", "==", "lobby"),
      orderBy("accessCode", "desc"), // or any other sortable field like createdAt
      limit(pageSize)
    );

    const paginatedQuery = startAfterDoc
      ? query(baseQuery, startAfter(startAfterDoc))
      : baseQuery;

    console.log("paginatedQuery", paginatedQuery);

    const snapshot = await getDocs(paginatedQuery);
    console.log("snapshot");

    const rooms = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    console.log("rooms from function call", rooms);

    const lastDoc = snapshot.docs[snapshot.docs.length - 1] ?? null;

    return { rooms, lastDoc };
  } catch (error) {
    console.error("🔥 Error in getLobbyRooms:", error);
    return { rooms: [], lastDoc: null };
  }
};

// Subscribe to real-time updates on a room
export const subscribeToRoom = (roomId, callback) => {
  const unsub = onSnapshot(doc(db, "rooms", roomId), (docSnap) => {
    if (docSnap.exists()) {
      callback({ id: docSnap.id, ...docSnap.data() });
    }
  });

  return unsub; // Call this to unsubscribe
};

// export const updatePlayerPresence = (userId, roomId) => {
//   const rtdb = getDatabase();
//   const presenceRef = ref(rtdb, `presence/${userId}`);

//   // Set presence data
//   set(presenceRef, {
//     inGameRoom: true,
//     online: true,
//     roomId,
//     lastSeen: Date.now(),
//   });

//   // Remove on disconnect
//   onDisconnect(presenceRef).remove();
// };

export function updatePlayerPresence(
  userData,
  {
    ...Payload
    // partyId = "",
    // host = false,
    // inGameRoom = false,
    // roomId = "",
    // emote = "",
    // x = 0,
    // y = 0,
    // direction = 90,
    // animState = "idle",
    // device = "mobile",
    // building = "",
  }
) {
  const db = getDatabase();
  const presRef = ref(db, `presence/${userData.id}`);

  const payload = {
    ...Payload,
    lastUpdated: Date.now(),
  };

  console.log("Payload", payload);

  // Write presence
  update(presRef, payload);

  // Clean up on disconnect
  onDisconnect(presRef).remove();
}

/**
 * Listen to everyone in the same partyId.
 * callback(type, userId, data?)
 *   type: "added" | "changed" | "removed"
 */
export function subscribeToPartyPresence(partyId, callback) {
  const db = getDatabase();
  const presRef = ref(db, "presence");
  const q = QUERY(presRef, orderByChild("partyId"), equalTo(partyId));

  const addedFn = (snapshot) => {
    const data = snapshot.val();
    console.log("[subscribeToPartyPresence] 🔵 added:", snapshot.key, data);
    callback("added", snapshot.key, data);
  };

  const changedFn = (snapshot) => {
    const data = snapshot.val();
    console.log("[subscribeToPartyPresence] 🟡 changed:", snapshot.key, data);
    callback("changed", snapshot.key, data);
  };

  const removedFn = (snapshot) => {
    console.log("[subscribeToPartyPresence] 🔴 removed:", snapshot.key);
    callback("removed", snapshot.key);
  };

  onChildAdded(q, addedFn);
  onChildChanged(q, changedFn);
  onChildRemoved(q, removedFn);

  return () =>
    off(q, "child_added", addedFn) &
    off(q, "child_changed", changedFn) &
    off(q, "child_removed", removedFn);
}

export const getAvailableHuman = async (players, roomId) => {
  console.log("checking avail humans");
  const rtdb = getDatabase();
  const humans = players.filter((p) => !p.isBot);

  for (let player of humans) {
    const presenceSnap = await get(ref(rtdb, `presence/${player.id}`));
    const presence = presenceSnap.val();

    console.log("Presence =>", presence, "Player is available", player);

    if (presence?.roomId === roomId && presence?.inGameRoom) {
      return player;
    }
  }

  return null;
};

export const savePlayerDeckAndHand = async (roomId, userId, { deck, hand }) => {
  try {
    const deckRef = doc(db, "rooms", roomId, "decks", userId);
    const handRef = doc(db, "rooms", roomId, "hands", userId);

    const updates = [];

    if (deck !== null) {
      updates.push(setDoc(deckRef, { cards: deck }, { merge: true }));
    }

    if (hand !== null) {
      updates.push(setDoc(handRef, { cards: hand }, { merge: true }));
    }

    await Promise.all(updates);

    console.log(`🔥 [Deck+Hand] Saved for user ${userId} in room ${roomId}`);
  } catch (err) {
    console.error("🚨 Failed to save deck/hand:", err);
  }
};

export const updateHandAndDeck = async (
  roomId,
  playerId,
  hand,
  deck = null
) => {
  const updates = [
    updateDoc(doc(db, "rooms", roomId, "hands", playerId), { cards: hand }),
  ];

  if (deck) {
    updates.push(
      updateDoc(doc(db, "rooms", roomId, "decks", playerId), { cards: deck })
    );
  }

  return Promise.all(updates);
};
